import type { Producto } from '@/tipos';

/**
 * Lo que el sitio le dice a Google en un idioma que Google entiende.
 *
 * El HTML dice "ACS001 — CARGADOR USB PARA MOTO" y una persona sabe que eso es un producto con su
 * código. Un buscador no: ve un título. Estos bloques —el formato se llama schema.org— nombran
 * cada cosa: esto es un producto, este es su código, esta es su marca, este es el camino desde el
 * inicio hasta acá.
 *
 * Todo sale de lo que ya está en la página. Un dato acá que no esté a la vista es lo que Google
 * llama "structured data mismatch" y penaliza, así que nada se inventa: el precio, por ejemplo,
 * no va, porque el sitio sólo lo muestra a quien inició sesión.
 */

export const SITIO = 'https://cking.com.ar';

/** Un escalón del camino: el nombre que se lee y la dirección adonde lleva. */
export interface Miga {
  nombre: string;
  ruta: string;
}

/**
 * El camino desde el inicio hasta la página, que es lo que Google dibuja arriba del resultado.
 *
 * "Inicio" va siempre y no se pasa: es el primer escalón de todos los caminos del sitio.
 */
export function migasDePan(camino: Miga[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{ nombre: 'Inicio', ruta: '/' }, ...camino].map((miga, indice) => ({
      '@type': 'ListItem',
      position: indice + 1,
      name: miga.nombre,
      item: `${SITIO}${miga.ruta}`,
    })),
  };
}

/**
 * Una lista de productos, en el orden en que se ven.
 *
 * Para las páginas de rubro: le dice a Google que eso es una lista de tantos productos y cuáles
 * son, en vez de un montón de links sueltos.
 */
export function listaDeProductos(productos: Producto[], nombre: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: nombre,
    numberOfItems: productos.length,
    itemListElement: productos.map((producto, indice) => ({
      '@type': 'ListItem',
      position: indice + 1,
      url: `${SITIO}/catalogo/${encodeURIComponent(producto.code)}`,
      name: `${producto.code} — ${producto.description}`,
    })),
  };
}

/**
 * Un producto: su código, su marca, su rubro y su foto.
 *
 * Sin `offers`, a propósito. Un `offers` sin precio no es válido para Google, y el precio de
 * CKING es mayorista: se ve iniciando sesión, no en la página pública. Poner un precio inventado
 * —o cero— para que el bloque "quede completo" es exactamente lo que hace que Google deje de
 * confiar en todo lo demás.
 */
export function comoProducto(producto: Producto, foto: string | null, rubro: string | null) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${producto.code} — ${producto.description}`,
    description: producto.description,
    sku: producto.code,
    mpn: producto.code,
    url: `${SITIO}/catalogo/${encodeURIComponent(producto.code)}`,
    ...(foto ? { image: foto.startsWith('http') ? foto : `${SITIO}${foto}` } : {}),
    ...(producto.brand ? { brand: { '@type': 'Brand', name: producto.brand } } : {}),
    ...(rubro ? { category: rubro } : {}),
  };
}
